import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContextType, UserProfile } from '@/shared/types/auth'
import { saveToLocalStorage } from '@/shared/utils/localStorageHelper'
import { useAlertService } from '@/shared/lib/useAlertService'
import { AuthService } from '@/shared/api/rest/services/auth.service'
import { BranchesService } from '@/shared/api/rest/services/branches.service'
import { tokenStore, setUnauthorizedHandler } from '@/shared/api/rest/client'
import { meToUserProfile } from '@/shared/api/rest/adapters'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate()
    const [user, setUser] = useState<UserProfile | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const alertService = useAlertService()

    const logout = useCallback(async (): Promise<void> => {
        tokenStore.clear()
        setUser(null)
        if (typeof window !== 'undefined') {
            localStorage.clear()
        }
        navigate('/')
    }, [navigate])

    const refreshProfile = useCallback(async (): Promise<void> => {
        try {
            const token = tokenStore.getAccessToken()
            if (!token) {
                return
            }

            const me = await AuthService.getMe()

            // Enrich with the parent record + branch name only when relevant.
            const isParent = Boolean(me.parentId)
            const [parent, branches] = await Promise.all([
                isParent ? AuthService.getParentMe() : Promise.resolve(null),
                isParent ? BranchesService.list().catch(() => []) : Promise.resolve([])
            ])

            const profile = meToUserProfile(me, parent, branches)
            setUser(profile)

            if (profile.parent?.branch?._id) {
                saveToLocalStorage('currentBranch', {
                    value: profile.parent.branch._id,
                    label: profile.parent.branch.name
                })
            }
        } catch (error) {
            console.error(
                'Exception in refreshProfile:',
                error instanceof Error ? error.message : error
            )
            setUser(null)
            tokenStore.clear()
        }
    }, [])

    const login = useCallback(
        async (email: string, password: string): Promise<void> => {
            try {
                setIsLoading(true)
                const data = await AuthService.login({ email, password })

                if (!data?.accessToken) {
                    alertService.error('Login failed')
                    return
                }

                tokenStore.setTokens(data.accessToken, data.refreshToken)

                await refreshProfile()

                navigate('/user/dashboard')
            } catch (error) {
                alertService.error(
                    error instanceof Error ? error.message : 'Login failed'
                )
                tokenStore.clear()
                throw error
            } finally {
                setIsLoading(false)
            }
        },
        [alertService, refreshProfile, navigate]
    )

    useEffect(() => {
        // When a token refresh fails mid-session, clear auth and bounce home.
        setUnauthorizedHandler(() => {
            setUser(null)
            tokenStore.clear()
            navigate('/')
        })
    }, [navigate])

    useEffect(() => {
        async function initializeAuth() {
            const token = tokenStore.getAccessToken()
            if (token) {
                await refreshProfile()
            }
            setIsLoading(false)
        }

        initializeAuth()
    }, [refreshProfile])

    const value: AuthContextType = {
        user,
        userTypes: user?.userTypes || null,
        isAuthenticated: Boolean(user),
        isLoading,
        login,
        logout,
        refreshProfile
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
